/**
 * Turn common REST validation JSON into short, toast-friendly strings (no raw JSON).
 */

function cleanDetailPart(s: string): string {
  return s.replace(/\\"/g, '"').trim();
}

function appendFieldMessages(
  sink: string[],
  fieldKey: string,
  val: unknown
): void {
  const label =
    fieldKey.length > 0
      ? fieldKey.charAt(0).toUpperCase() +
        fieldKey.slice(1).replace(/_/g, " ")
      : fieldKey;
  if (Array.isArray(val)) {
    for (const item of val) {
      if (typeof item === "string" && item.trim()) {
        const text = cleanDetailPart(item);
        sink.push(label ? `${label}: ${text}` : text);
      }
    }
    return;
  }
  if (typeof val === "string" && val.trim()) {
    const text = cleanDetailPart(val);
    sink.push(label ? `${label}: ${text}` : text);
  }
}

/**
 * Returns a single-line or short multi-part message, or null if the body has no known shape.
 */
export function formatApiErrorBody(body: unknown): string | null {
  if (body == null) {
    return null;
  }
  if (typeof body === "string") {
    const t = body.trim();
    return t.length ? t : null;
  }
  if (typeof body !== "object" || Array.isArray(body)) {
    return null;
  }

  const o = body as Record<string, unknown>;

  const detail = o.detail;
  if (typeof detail === "string" && detail.trim()) {
    return detail.trim();
  }
  if (Array.isArray(detail)) {
    const parts: string[] = [];
    for (const item of detail) {
      if (typeof item === "string" && item.trim()) {
        parts.push(cleanDetailPart(item));
      } else if (item && typeof item === "object" && !Array.isArray(item)) {
        const msg = (item as Record<string, unknown>).msg;
        if (typeof msg === "string" && msg.trim()) {
          parts.push(cleanDetailPart(msg));
        }
      }
    }
    if (parts.length) {
      return parts.join(" ");
    }
  }

  if (typeof o.message === "string" && o.message.trim()) {
    return o.message.trim();
  }

  const err = o.error;
  if (typeof err === "string" && err.trim()) {
    return err.trim();
  }

  if (err && typeof err === "object" && !Array.isArray(err)) {
    const e = err as Record<string, unknown>;
    const fromDetails: string[] = [];
    const details = e.details;
    if (details && typeof details === "object" && !Array.isArray(details)) {
      for (const [field, val] of Object.entries(details)) {
        appendFieldMessages(fromDetails, field, val);
      }
    }

    if (fromDetails.length) {
      return [...new Set(fromDetails)].join(" ");
    }

    const em = e.message;
    if (typeof em === "string" && em.trim()) {
      return em.trim();
    }

    const code = e.code;
    if (typeof code === "string" && code.trim()) {
      return code.trim().replace(/_/g, " ");
    }
  }

  return null;
}

/**
 * Safe label for toasts/alerts: extracts nested API `error.message`, validation `details`, etc.
 * Never surfaces raw JSON when the payload is parsable.
 */
export function humanizeApiErrorText(
  input: unknown,
  fallback = "Something went wrong. Please try again."
): string {
  if (input instanceof Error) {
    const m = input.message?.trim();
    if (!m) {
      return fallback;
    }
    return humanizeApiErrorText(m, fallback);
  }
  if (input === null || input === undefined) {
    return fallback;
  }
  if (typeof input === "object") {
    return formatApiErrorBody(input) ?? fallback;
  }
  if (typeof input === "string") {
    const trimmed = input.trim();
    if (!trimmed) {
      return fallback;
    }
    const looksJson =
      (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
      (trimmed.startsWith("[") && trimmed.endsWith("]"));
    if (looksJson) {
      try {
        const parsed = JSON.parse(trimmed) as unknown;
        return formatApiErrorBody(parsed) ?? trimmed.slice(0, 280);
      } catch {
        return trimmed.slice(0, 400);
      }
    }
    return trimmed;
  }
  return String(input);
}
